import { useEffect, useState } from 'react';
import type { UnifiedTaskItem, UnifiedTaskView, UnifiedWorkGroup } from '../utils/unifiedTasks';
import { groupUnifiedWorkItems } from '../utils/unifiedTasks';
import type { AgentIdentity } from '../utils/agentLabels';
import { AgentChip } from './AgentChip';
import styles from '../pages/UnifiedTasksPage.module.css';

type TaskTone = 'attention' | 'inProgress' | 'upcoming' | 'done' | 'failed';

const STATUS_DOT_CLASS: Record<TaskTone, string> = {
  attention: styles.statusDotAttention,
  inProgress: styles.statusDotInProgress,
  upcoming: styles.statusDotUpcoming,
  done: styles.statusDotDone,
  failed: styles.statusDotFailed,
};

const STATUS_PILL_CLASS: Record<TaskTone, string> = {
  attention: styles.statusPillAttention,
  inProgress: styles.statusPillInProgress,
  upcoming: styles.statusPillUpcoming,
  done: styles.statusPillDone,
  failed: styles.statusPillFailed,
};

function sameLocalDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

function formatDate(value: number): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** "40s ago", "12m ago", "3h ago": the largest whole unit, for a time earlier today. */
function elapsedToday(at: number, now: number): string {
  const seconds = Math.max(0, Math.floor((now - at) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

function dateLabel(item: UnifiedTaskItem, now: number): { text: string; overdue: boolean } | null {
  if (item.lastUpdatedAt !== null) {
    const date = formatDate(item.lastUpdatedAt);
    if (!date) return null;
    const updatedDate = new Date(item.lastUpdatedAt);
    const today = new Date(now);
    return {
      text: sameLocalDay(updatedDate, today)
        ? `Updated ${elapsedToday(item.lastUpdatedAt, now)}`
        : `Updated ${date}`,
      overdue: false,
    };
  }
  return null;
}

function tone(item: UnifiedTaskItem): TaskTone {
  if (item.status === 'failed') return 'failed';
  if (item.stage === 'needs_attention') return 'attention';
  if (item.stage === 'in_progress') return 'inProgress';
  return 'done';
}

function groupLabel(group: UnifiedWorkGroup): string {
  return group.key === 'queued' ? 'Queued' : 'In progress';
}

function TaskRow({
  item,
  selectedId,
  onSelect,
  now,
  agents,
}: {
  item: UnifiedTaskItem;
  selectedId: string | null;
  onSelect: (id: string) => void;
  now: number;
  agents?: readonly AgentIdentity[];
}) {
  const isSelected = item.id === selectedId;
  const itemTone = tone(item);
  const date = dateLabel(item, now);

  return (
    <button
      type="button"
      className={isSelected ? `${styles.taskRow} ${styles.taskRowSelected}` : styles.taskRow}
      onClick={() => onSelect(item.id)}
      aria-pressed={isSelected}
      aria-current={isSelected ? 'true' : undefined}
      aria-label={`${item.title}, ${item.statusLabel}${date ? `, ${date.text}` : ''}`}
    >
      <span aria-hidden="true" className={`${styles.statusDot} ${STATUS_DOT_CLASS[itemTone]}`} />
      <span className={styles.taskRowBody}>
        <span className={styles.taskRowTitle}>{item.title}</span>
        {item.typeLabel !== item.title && (
          <span className={styles.taskRowType}>{item.typeLabel}</span>
        )}
        <span className={styles.taskRowFooter}>
          <AgentChip agentType={item.agentType} agents={agents} />
          <span className={`${styles.statusPill} ${STATUS_PILL_CLASS[itemTone]}`}>
            {item.statusLabel}
          </span>
          {date && (
            <span
              className={
                date.overdue ? `${styles.dueDate} ${styles.dueDateOverdue}` : styles.dueDate
              }
            >
              {date.text}
            </span>
          )}
        </span>
      </span>
    </button>
  );
}

function TaskGroup({
  group,
  selectedId,
  onSelect,
  now,
  agents,
}: {
  group: UnifiedWorkGroup;
  selectedId: string | null;
  onSelect: (id: string) => void;
  now: number;
  agents?: readonly AgentIdentity[];
}) {
  return (
    <section className={styles.group} aria-labelledby={`task-group-${group.key}`}>
      <h3 id={`task-group-${group.key}`} className={styles.groupHeading}>
        <span>{groupLabel(group)}</span>
        <span className={styles.groupCount}>{group.items.length}</span>
      </h3>
      {group.items.map((item) => (
        <TaskRow
          key={item.id}
          item={item}
          selectedId={selectedId}
          onSelect={onSelect}
          now={now}
          agents={agents}
        />
      ))}
    </section>
  );
}

/** The current time, once a second: rows show "Updated 40s ago", which a minute-long tick leaves stale. */
function useSecondClock(): number {
  const [time, setTime] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setTime(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);
  return time;
}

export function UnifiedTaskList({
  items,
  view,
  selectedId,
  onSelect,
  now: pageNow,
  agents,
}: {
  items: UnifiedTaskItem[];
  view: UnifiedTaskView;
  selectedId: string | null;
  onSelect: (id: string) => void;
  now: number;
  /** The location's assigned agents, for naming each row's chip. */
  agents?: readonly AgentIdentity[];
}) {
  const clock = useSecondClock();
  const now = Math.max(pageNow, clock);
  if (items.length === 0) {
    return (
      <div className={styles.emptyWorkspace}>
        <div className={styles.emptyWorkspaceContent}>
          <span className={styles.emptyWorkspaceTitle}>No matching tasks</span>
          <span>Try another view or clear the search.</span>
        </div>
      </div>
    );
  }

  if (view !== 'work') {
    return (
      <div className={styles.listScroll}>
        {items.map((item) => (
          <TaskRow
            key={item.id}
            item={item}
            selectedId={selectedId}
            onSelect={onSelect}
            now={now}
            agents={agents}
          />
        ))}
      </div>
    );
  }

  const groups = groupUnifiedWorkItems(items);

  return (
    <div className={styles.listScroll}>
      {groups.map((group) => (
        <TaskGroup
          key={group.key}
          group={group}
          selectedId={selectedId}
          onSelect={onSelect}
          now={now}
          agents={agents}
        />
      ))}
    </div>
  );
}

export function UnifiedTaskListHeader({ label, count }: { label: string; count: number }) {
  return (
    <div className={styles.listHeader}>
      <span className={styles.listHeaderTitle}>{label}</span>
      <span>
        {count} task{count === 1 ? '' : 's'}
      </span>
    </div>
  );
}
